function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
import React, { ReactNode } from 'react';
import { AxisTypeValue } from 'highcharts';
import clamp from 'lodash/clamp';
import cloneDeep from 'lodash/cloneDeep';
import mergeWith from 'lodash/mergeWith';
import { InputReact } from 'common/components/input';
import { BehaviorMetric, BookmarkXAxisOptions, BookmarkYAxisOptions, ShowClause } from 'common/report/insights/util/types';
import { ToggleReact } from 'common/components/toggle';
import * as BookmarkTypes from 'common/types/reports/bookmark';
import { ToggleSize, ToggleTheme } from 'common/components/toggle/types';
import { BehaviorShowClause } from 'common/report/insights/models/show-clause';
import { displayOptionsMatchAxis, isBehaviorShowClause, isFormulaShowClause } from 'common/report/insights/models/utils';
import { InputSize } from 'common/components/input/types';
import { SvgIconReact } from 'common/components/svg-icon';
import { ICON_MAPPING } from 'common/query-builder/query-entry-section/util/entry-icon';
import { getAlphaLabelFromIndex } from 'common/util';
import { ButtonReact } from 'common/components/button';
import { ButtonLayout, ButtonSize, ButtonTheme } from 'common/components/button/constants';
import { useTranslation } from 'common/i18n/hooks';
import { eventDefinitionsStore } from 'common/report/stores/event-definitions';
import { useMetrics } from 'common/contexts/react/metrics-resource-context';
import { MetricsResource } from 'common/report/model-resources/metrics';
import { TooltipReact } from 'common/components/tooltip';
import { dataGroupStore } from 'common/report/stores/data-groups';
import { MetricUnitsSelector } from 'iron/common/query-builder/chart-customization/metric-units';
import { axisSettingsHaveChanged, getDisplayOptionsFromLineChartYAxisOptions } from 'common/report/insights/charts/util';
import { EMPTY_BOOKMARK_Y_AXIS_OPTIONS } from 'common/report/insights/util/constants';
import { AnalysisType, ChartType, MetricType } from 'common/types/reports/bookmark';
import { getMetricEntryBehavior } from 'common/query-builder/query-entry-section/row-types/metric-entry/metrics/metric-entry-util';
import { getDefaultPrimaryAndSecondaryAxisOptions, getXAxisDefaultOptions } from '../utils';
import styles from '../index.module.styl';
import { CallingLocation, track, TrackingEvent } from '../tracking';
import { CLEAR_VALUE, UNSET_VALUE } from './constants';
import { ChartCustomizationAxisProps, ChartCustomizationUpdateType } from './types';
const NUMBER_BOUNDS = {
  MAX: Number.MAX_SAFE_INTEGER,
  MIN: Number.MIN_SAFE_INTEGER
};

// This callback makes sure that if a value is UNSET_VALUE, it is set to
// UNSET_VALUE in the object. Lodash's default behavior ignores "undefined"
// values which will break the merge.
const mergeWithCallback = (_objValue, srcValue, key, object) => {
  if (srcValue === UNSET_VALUE) {
    object[key] = UNSET_VALUE;
  }
};

/**
 * Makes sure the value is a number within the bounds of the safe integer range
 * and also checks for unset values.
 * @param value
 * @returns null | NaN | number
 */
const getSafeNumber = (value: string | undefined) => {
  const numValue = Number(value);
  if (value === undefined || value === "") {
    return CLEAR_VALUE;
  }
  return clamp(numValue, NUMBER_BOUNDS.MIN, NUMBER_BOUNDS.MAX);
};
type YAxisSettingProps = {
  axisSetting: BookmarkYAxisOptions;
  setAxisSetting: (setting: Partial<BookmarkYAxisOptions>) => void;
  inheritedPrecision?: number;
};
enum AxisVisibility {
  Visible = "visible",
  Hidden = "hidden",
}
const getDisplayValue = (value: number | undefined) => value === undefined || value === null ? "" : String(value);
const HeaderRow = (_ref: {
  children: ReactNode;
}) => {
  let {
    children
  }: {
    children: ReactNode;
  } = _ref;
  return <h1 className={styles.ccAxisLabel} data-sentry-component="HeaderRow" data-sentry-source-file="index.tsx">{children}</h1>;
};
const getEventName = (clauseType: MetricType, clauseName: string, clauseId: number, metricsContext: MetricsResource) => {
  var _eventDefinitionsStor;
  if (clauseType === MetricType.Formula) {
    var _metricsContext$getOn;
    return clauseName || ((_metricsContext$getOn = metricsContext.getOne(clauseId)) === null || _metricsContext$getOn === void 0 || (_metricsContext$getOn = _metricsContext$getOn.value) === null || _metricsContext$getOn === void 0 ? void 0 : _metricsContext$getOn.name);
  }
  const eventDefinition = (_eventDefinitionsStor = eventDefinitionsStore.value) === null || _eventDefinitionsStor === void 0 ? void 0 : _eventDefinitionsStor[clauseName];
  return (eventDefinition === null || eventDefinition === void 0 ? void 0 : eventDefinition.displayName) || (eventDefinition === null || eventDefinition === void 0 ? void 0 : eventDefinition.name) || clauseName || clauseType;
};
const VisibilityRow = (_ref2: YAxisSettingProps) => {
  var _axisSetting$labels;
  let {
    axisSetting,
    setAxisSetting
  }: YAxisSettingProps = _ref2;
  const {
    t
  } = useTranslation();
  return <div className={styles.row} data-sentry-component="VisibilityRow" data-sentry-source-file="index.tsx">
      <div style={{
      gridColumn: 1
    }}>
        <label className={styles.ccSublabel}>{t("chart-customization.axis")}</label>
      </div>
      <div className={styles.singleInputColumn}>
        <ToggleReact className={styles.singleInput} size={ToggleSize.Medium} theme={ToggleTheme.SegmentedController} options={[{
        label: t("common.visible"),
        value: AxisVisibility.Visible
      }, {
        label: t("common.hidden"),
        value: AxisVisibility.Hidden
      }]} selected={((_axisSetting$labels = axisSetting.labels) === null || _axisSetting$labels === void 0 ? void 0 : _axisSetting$labels.enabled) === false ? AxisVisibility.Hidden : AxisVisibility.Visible} onSelect={ev => {
        setAxisSetting({
          labels: {
            enabled: ev.detail.selected === AxisVisibility.Visible
          }
        });
      }} data-sentry-element="ToggleReact" data-sentry-source-file="index.tsx" />
      </div>
    </div>;
};
const LabelRow = (_ref3: {
  axisSetting: BookmarkYAxisOptions;
  setAxisSetting: (setting: Partial<BookmarkYAxisOptions>) => void;
  placeholder?: string;
}) => {
  var _axisSetting$title;
  let {
    axisSetting,
    setAxisSetting,
    placeholder
  }: {
    axisSetting: BookmarkYAxisOptions;
    setAxisSetting: (setting: Partial<BookmarkYAxisOptions>) => void;
    placeholder?: string;
  } = _ref3;
  const {
    t
  } = useTranslation();
  return <div className={styles.row} data-sentry-component="LabelRow" data-sentry-source-file="index.tsx">
      <div style={{
      gridColumn: 1
    }}>
        <label className={styles.ccSublabel}>{t("common.label")}</label>
      </div>
      <div className={styles.singleInputColumn}>
        <InputReact ariaLabel={t("common.label")} className={styles.singleInput} size={InputSize.Medium} placeholder={placeholder} value={((_axisSetting$title = axisSetting.title) === null || _axisSetting$title === void 0 ? void 0 : _axisSetting$title.text) || ""} onChange={ev => setAxisSetting({
        title: {
          text: ev.detail.value || null
        }
      })} data-sentry-element="InputReact" data-sentry-source-file="index.tsx" />
      </div>
    </div>;
};
const ToggleSoftMinMaxRow = (_ref4: YAxisSettingProps) => {
  let {
    axisSetting,
    setAxisSetting
  }: YAxisSettingProps = _ref4;
  const {
    t
  } = useTranslation();
  return <div className={styles.row} data-sentry-component="ToggleSoftMinMaxRow" data-sentry-source-file="index.tsx">
      <div style={{
      gridColumn: 1
    }}>
        <label className={styles.ccSublabel}>
          {t("chart-customization.strict-threshold")}
          <SvgIconReact icon='info' className={styles.infoIcon} data-sentry-element="SvgIconReact" data-sentry-source-file="index.tsx" />
          <TooltipReact data-sentry-element="TooltipReact" data-sentry-source-file="index.tsx">{t("chart-customization.strict-threshold-tooltip")}</TooltipReact>
        </label>
      </div>
      <div style={{
      gridColumn: "2 / span 2"
    }}>
        <ToggleReact className={styles.singleInput} size={ToggleSize.Medium} theme={ToggleTheme.SegmentedController} options={[{
        label: t("common.visible"),
        value: "true"
      }, {
        label: t("common.hidden"),
        value: "false"
      }]} selected={(axisSetting.useSoftMinMax || false) + ""} onSelect={ev => {
        setAxisSetting({
          useSoftMinMax: ev.detail.selected === "true"
        });
      }} data-sentry-element="ToggleReact" data-sentry-source-file="index.tsx" />
      </div>
    </div>;
};
const ScaleRow = (_ref5: YAxisSettingProps) => {
  let {
    axisSetting,
    setAxisSetting
  }: YAxisSettingProps = _ref5;
  const {
    t
  } = useTranslation();
  return <div className={styles.row} data-sentry-component="ScaleRow" data-sentry-source-file="index.tsx">
      <div style={{
      gridColumn: 1
    }}>
        <label className={styles.ccSublabel}>{t("chart-customization.scale")}</label>
      </div>
      <div style={{
      gridColumn: "2 / span 2"
    }}>
        <ToggleReact className={styles.singleInput} size={ToggleSize.Medium} theme={ToggleTheme.SegmentedController} options={[{
        label: t("reports.analysis-types.linear"),
        value: AnalysisType.Linear
      }, {
        label: t("reports.analysis-types.logarithmic"),
        value: AnalysisType.Logarithmic
      }]} selected={axisSetting.type || AnalysisType.Linear} onSelect={ev => {
        setAxisSetting({
          type: ev.detail.selected as AxisTypeValue
        });
      }} data-sentry-element="ToggleReact" data-sentry-source-file="index.tsx" />
      </div>
    </div>;
};
const ThresholdRow = (_ref6: YAxisSettingProps & {
  defaultMinValue?: number;
  defaultMaxValue?: number;
}) => {
  let {
    axisSetting,
    setAxisSetting,
    defaultMinValue,
    defaultMaxValue
  }: YAxisSettingProps & {
    defaultMinValue?: number;
    defaultMaxValue?: number;
  } = _ref6;
  const {
    t
  } = useTranslation();
  const minPlaceholderValue = defaultMinValue !== undefined ? getDisplayValue(defaultMinValue) : undefined;
  const maxPlaceholderValue = defaultMaxValue !== undefined ? getDisplayValue(defaultMaxValue) : undefined;
  return <div className={styles.row} data-sentry-component="ThresholdRow" data-sentry-source-file="index.tsx">
      <div style={{
      gridColumn: 1
    }}>
        <label className={styles.ccSublabel}>{t("chart-customization.threshold")}</label>
      </div>
      <div style={{
      gridColumn: 2
    }}>
        <InputReact ariaLabel={t("chart-customization.min")} className={styles.twoInputColumn} size={InputSize.Medium} placeholder={minPlaceholderValue || t("chart-customization.min")} value={getDisplayValue(axisSetting.min)} onChange={ev => {
        const number = getSafeNumber(ev.detail.value);
        if (!isNaN(number)) {
          setAxisSetting({
            min: number
          });
        }
      }} data-sentry-element="InputReact" data-sentry-source-file="index.tsx" />
      </div>
      <div style={{
      gridColumn: 3
    }}>
        <InputReact ariaLabel={t("chart-customization.max")} className={styles.twoInputColumn} size={InputSize.Medium} placeholder={maxPlaceholderValue || t("chart-customization.max")} value={getDisplayValue(axisSetting.max)} onChange={ev => {
        const number = getSafeNumber(ev.detail.value);
        if (!isNaN(number)) {
          setAxisSetting({
            max: number
          });
        }
      }} data-sentry-element="InputReact" data-sentry-source-file="index.tsx" />
      </div>
    </div>;
};
export function ChartAxisCustomization(props: ChartCustomizationAxisProps) {
  var _props$bookmarkParams, _props$bookmarkParams2, _props$bookmarkParams3, _props$bookmarkParams4, _defaultXOptions$titl;
  const {
    t
  } = useTranslation();
  const metricsContext = useMetrics();
  const defaultXOptions = getXAxisDefaultOptions(props.bookmarkParams, dataGroupStore);
  const showClauses = props.bookmarkParams.sections.show || [];
  const {
    defaultPrimaryOptions,
    defaultSecondaryOptions
  } = getDefaultPrimaryAndSecondaryAxisOptions(props.bookmarkParams, metricsContext);
  const primaryYSetting: BookmarkYAxisOptions = ((_props$bookmarkParams = props.bookmarkParams) === null || _props$bookmarkParams === void 0 || (_props$bookmarkParams = _props$bookmarkParams.displayOptions) === null || _props$bookmarkParams === void 0 ? void 0 : _props$bookmarkParams.primaryYAxisOptions) || {};
  const secondaryYSetting: BookmarkYAxisOptions = ((_props$bookmarkParams2 = props.bookmarkParams) === null || _props$bookmarkParams2 === void 0 || (_props$bookmarkParams2 = _props$bookmarkParams2.displayOptions) === null || _props$bookmarkParams2 === void 0 ? void 0 : _props$bookmarkParams2.secondaryYAxisOptions) || {};
  const xSetting: BookmarkXAxisOptions = ((_props$bookmarkParams3 = props.bookmarkParams) === null || _props$bookmarkParams3 === void 0 || (_props$bookmarkParams3 = _props$bookmarkParams3.displayOptions) === null || _props$bookmarkParams3 === void 0 ? void 0 : _props$bookmarkParams3.xAxisOptions) || {
    labels: {
      enabled: true
    }
  };
  const setPrimaryYSetting = (updated: Partial<BookmarkYAxisOptions>) => {
    props.onUpdate(ChartCustomizationUpdateType.PrimaryYAxis, {
      primaryYAxis: updated,
      secondaryYAxis: secondaryYSetting,
      xAxis: xSetting,
      newShowClauses: showClauses
    }, updated, primaryYSetting);
  };
  const resetPrimaryYSetting = () => {
    setPrimaryYSetting(EMPTY_BOOKMARK_Y_AXIS_OPTIONS);
  };
  const updatePrimaryYSetting = (setting: Partial<BookmarkYAxisOptions>) => {
    const updated = mergeWith(cloneDeep(primaryYSetting), setting, mergeWithCallback);
    setPrimaryYSetting(updated);
  };
  const setSecondaryYSetting = (updated: Partial<BookmarkYAxisOptions>) => {
    props.onUpdate(ChartCustomizationUpdateType.SecondaryYAxis, {
      primaryYAxis: primaryYSetting,
      secondaryYAxis: updated,
      xAxis: xSetting,
      newShowClauses: showClauses
    }, updated, secondaryYSetting);
  };
  const resetSecondaryYSetting = () => {
    setSecondaryYSetting(EMPTY_BOOKMARK_Y_AXIS_OPTIONS);
  };
  const updateSecondaryYSetting = (setting: Partial<BookmarkYAxisOptions>) => {
    const updated = mergeWith(cloneDeep(secondaryYSetting), setting, mergeWithCallback);
    setSecondaryYSetting(updated);
  };
  const setXSetting = (setting: Partial<BookmarkXAxisOptions>) => {
    const updated = mergeWith(cloneDeep(xSetting), setting);
    props.onUpdate(ChartCustomizationUpdateType.XAxis, {
      primaryYAxis: primaryYSetting,
      secondaryYAxis: secondaryYSetting,
      xAxis: updated
    }, setting, primaryYSetting);
  };
  const getUpdatedClause = (oldClause: any, axisAssignment: BookmarkTypes.AxisAssignment) => {
    const newClause = cloneDeep(oldClause);
    if (!newClause.display) {
      newClause.display = {
        axis: BookmarkTypes.AxisAssignment.Primary
      };
    }
    newClause.display.axis = axisAssignment;
    return newClause;
  };
  const getUpdatedClauses = (hasNestedBehaviors: boolean, axisAssignment: BookmarkTypes.AxisAssignment, idx: number, oldClauses: ShowClause[]) => {
    const newClauses = cloneDeep(oldClauses);
    newClauses[idx] = getUpdatedClause(newClauses[idx], axisAssignment);
    if (hasNestedBehaviors) {
      var _newClauses$idx;
      const behaviors = ((_newClauses$idx = newClauses[idx] as BehaviorMetric) === null || _newClauses$idx === void 0 || (_newClauses$idx = _newClauses$idx.behavior) === null || _newClauses$idx === void 0 ? void 0 : _newClauses$idx.behaviors) || [];
      for (let i = 0; i < behaviors.length; i++) {
        behaviors[i] = getUpdatedClause(behaviors[i], axisAssignment);
      }
    }
    return newClauses;
  };
  const getClauseRow = (hasNestedBehaviors: boolean, idx: number, clause: ShowClause) => {
    const {
      clauseType,
      clauseName,
      clauseId
    } = isFormulaShowClause(clause) ? {
      clauseType: MetricType.Formula,
      clauseName: clause === null || clause === void 0 ? void 0 : clause.name,
      clauseId: clause === null || clause === void 0 ? void 0 : clause.id
    } : {
      clauseType: clause.behavior.type,
      clauseName: getMetricEntryBehavior(clause.behavior).name,
      clauseId: clause.behavior.id
    };
    const display = clause.display;
    const svgIcon = ICON_MAPPING[clauseType];
    return <div className={styles.axisAssignmentRow} key={idx} data-sentry-component="getClauseRow" data-sentry-source-file="index.tsx">
        <div style={{
        gridColumn: 1
      }}>
          <div style={{
          display: "flex",
          alignItems: "center"
        }}>
            <ButtonReact className={styles.eventPrefix} theme={ButtonTheme.Tertiary} notFocusable data-sentry-element="ButtonReact" data-sentry-source-file="index.tsx">
              <span className={styles.prefixText}>{getAlphaLabelFromIndex(idx)}</span>
            </ButtonReact>
            {svgIcon && <SvgIconReact className={styles.metricIcon} icon={svgIcon} />}
            <label>{getEventName(clauseType, clauseName, clauseId, metricsContext)}</label>
          </div>
        </div>
        <ToggleReact style={{
        width: "60px"
      }} size={ToggleSize.Medium} theme={ToggleTheme.SegmentedController} options={[{
        label: t("chart-customization.left-short"),
        value: BookmarkTypes.AxisAssignment.Primary
      }, {
        label: t("chart-customization.right-short"),
        value: BookmarkTypes.AxisAssignment.Secondary
      }]} selected={(display === null || display === void 0 ? void 0 : display.axis) || BookmarkTypes.AxisAssignment.Primary} onSelect={ev => {
        var _primaryYSetting$labe, _secondaryYSetting$la;
        const oldClauses = showClauses;
        const updatedClauses = getUpdatedClauses(hasNestedBehaviors, ev.detail.selected as BookmarkTypes.AxisAssignment, idx, oldClauses);

        // If assigning a metric to an axis that doesn't have any other metrics assigned to it, show the labels
        const allPrimary = !oldClauses.some(x => displayOptionsMatchAxis(x.display, BookmarkTypes.AxisAssignment.Secondary));
        const allSecondary = !oldClauses.some(x => displayOptionsMatchAxis(x.display, BookmarkTypes.AxisAssignment.Primary));
        const shouldShowPrimary = ((_primaryYSetting$labe = primaryYSetting.labels) === null || _primaryYSetting$labe === void 0 ? void 0 : _primaryYSetting$labe.enabled) === false && allSecondary;
        const shouldShowSecondary = ((_secondaryYSetting$la = secondaryYSetting.labels) === null || _secondaryYSetting$la === void 0 ? void 0 : _secondaryYSetting$la.enabled) === false && allPrimary;
        const updatedPrimary = shouldShowPrimary ? cloneDeep(_objectSpread(_objectSpread({}, primaryYSetting), {
          labels: {
            enabled: true
          }
        })) : primaryYSetting;
        const updatedSecondary = shouldShowSecondary ? cloneDeep(_objectSpread(_objectSpread({}, secondaryYSetting), {
          labels: {
            enabled: true
          }
        })) : secondaryYSetting;
        props.onUpdate(ChartCustomizationUpdateType.MetricAxisAssignment, {
          primaryYAxis: updatedPrimary,
          secondaryYAxis: updatedSecondary,
          xAxis: xSetting,
          newShowClauses: updatedClauses
        });
      }} data-sentry-element="ToggleReact" data-sentry-source-file="index.tsx" />
      </div>;
  };
  const xAxisDisabled = props.bookmarkParams.displayOptions.chartType === ChartType.FunnelSteps;
  const defaultAxisOptions = getDisplayOptionsFromLineChartYAxisOptions(defaultPrimaryOptions);
  const showPrimaryAxisReset = axisSettingsHaveChanged({
    current: primaryYSetting,
    defaults: defaultPrimaryOptions
  });
  const showSecondaryAxisReset = axisSettingsHaveChanged({
    current: secondaryYSetting,
    defaults: defaultSecondaryOptions
  });
  return <div elref='chartCustomizationAxis' data-sentry-component="ChartAxisCustomization" data-sentry-source-file="index.tsx">
      <div elref='leftAxis' className={styles.axisBlock} style={{
      marginTop: "16px"
    }}>
        <HeaderRow data-sentry-element="HeaderRow" data-sentry-source-file="index.tsx">
          {t("chart-customization.left-y-axis")}
          <ButtonReact class={showPrimaryAxisReset ? "" : styles.hideResetButton} disabled={!showPrimaryAxisReset} layout={ButtonLayout.Horizontal} onClick={() => {
          resetPrimaryYSetting();
          track(TrackingEvent.Reset, CallingLocation.Axis, {
            axis: primaryYSetting,
            axisAssignment: BookmarkTypes.AxisAssignment.Primary
          });
        }} size={ButtonSize.Small} theme={ButtonTheme.Tertiary} data-sentry-element="ButtonReact" data-sentry-source-file="index.tsx">
            {t("chart-customization.reset")}
          </ButtonReact>
        </HeaderRow>
        <div className={styles.axisSettingsBlock}>
          <VisibilityRow axisSetting={primaryYSetting} setAxisSetting={updatePrimaryYSetting} data-sentry-element="VisibilityRow" data-sentry-source-file="index.tsx" />
          <LabelRow axisSetting={primaryYSetting} setAxisSetting={updatePrimaryYSetting} data-sentry-element="LabelRow" data-sentry-source-file="index.tsx" />
          <MetricUnitsSelector axis={primaryYSetting} axisAssignment={BookmarkTypes.AxisAssignment.Primary} callingLocation={CallingLocation.Axis} chartType={(_props$bookmarkParams4 = props.bookmarkParams.displayOptions) === null || _props$bookmarkParams4 === void 0 ? void 0 : _props$bookmarkParams4.chartType} defaultAxisOptions={defaultAxisOptions} elref={"leftMetricUnitsSelector"} setAxis={updatePrimaryYSetting} data-sentry-element="MetricUnitsSelector" data-sentry-source-file="index.tsx" />
          <ThresholdRow axisSetting={primaryYSetting} setAxisSetting={updatePrimaryYSetting} defaultMinValue={defaultPrimaryOptions.softMin} defaultMaxValue={defaultPrimaryOptions.softMax} data-sentry-element="ThresholdRow" data-sentry-source-file="index.tsx" />
          <ToggleSoftMinMaxRow axisSetting={primaryYSetting} setAxisSetting={updatePrimaryYSetting} data-sentry-element="ToggleSoftMinMaxRow" data-sentry-source-file="index.tsx" />
          <ScaleRow axisSetting={primaryYSetting} setAxisSetting={updatePrimaryYSetting} data-sentry-element="ScaleRow" data-sentry-source-file="index.tsx" />
        </div>
      </div>

      <div elref='rightAxis' className={styles.axisBlock}>
        <HeaderRow data-sentry-element="HeaderRow" data-sentry-source-file="index.tsx">
          {t("chart-customization.right-y-axis")}
          <ButtonReact class={showSecondaryAxisReset ? "" : styles.hideResetButton} disabled={!showSecondaryAxisReset} layout={ButtonLayout.Horizontal} onClick={() => {
          resetSecondaryYSetting();
          track(TrackingEvent.Reset, CallingLocation.Axis, {
            axis: secondaryYSetting,
            axisAssignment: BookmarkTypes.AxisAssignment.Secondary
          });
        }} size={ButtonSize.Small} theme={ButtonTheme.Tertiary} data-sentry-element="ButtonReact" data-sentry-source-file="index.tsx">
            {t("chart-customization.reset")}
          </ButtonReact>
        </HeaderRow>
        <div className={styles.axisSettingsBlock}>
          <VisibilityRow axisSetting={secondaryYSetting} setAxisSetting={updateSecondaryYSetting} data-sentry-element="VisibilityRow" data-sentry-source-file="index.tsx" />
          <LabelRow axisSetting={secondaryYSetting} setAxisSetting={updateSecondaryYSetting} data-sentry-element="LabelRow" data-sentry-source-file="index.tsx" />
          <MetricUnitsSelector axis={secondaryYSetting} axisAssignment={BookmarkTypes.AxisAssignment.Secondary} callingLocation={CallingLocation.Axis} defaultAxisOptions={getDisplayOptionsFromLineChartYAxisOptions(defaultSecondaryOptions)} elref={"rightMetricUnitsSelector"} setAxis={updateSecondaryYSetting} data-sentry-element="MetricUnitsSelector" data-sentry-source-file="index.tsx" />
          <ThresholdRow axisSetting={secondaryYSetting} setAxisSetting={updateSecondaryYSetting} defaultMinValue={defaultSecondaryOptions.softMin} defaultMaxValue={defaultSecondaryOptions.softMax} data-sentry-element="ThresholdRow" data-sentry-source-file="index.tsx" />
          <ToggleSoftMinMaxRow axisSetting={secondaryYSetting} setAxisSetting={updateSecondaryYSetting} data-sentry-element="ToggleSoftMinMaxRow" data-sentry-source-file="index.tsx" />
          <ScaleRow axisSetting={secondaryYSetting} setAxisSetting={updateSecondaryYSetting} data-sentry-element="ScaleRow" data-sentry-source-file="index.tsx" />
        </div>
      </div>

      {!xAxisDisabled && <div className={styles.axisBlock}>
          <HeaderRow>{t("chart-customization.x-axis")}</HeaderRow>
          <div className={styles.axisSettingsBlock}>
            <VisibilityRow axisSetting={xSetting} setAxisSetting={setXSetting} />
            <LabelRow axisSetting={xSetting} setAxisSetting={setXSetting} placeholder={(_defaultXOptions$titl = defaultXOptions.title) === null || _defaultXOptions$titl === void 0 ? void 0 : _defaultXOptions$titl.placeholder} />
          </div>
        </div>}

      <div elref='axisAssignment'>
        <HeaderRow data-sentry-element="HeaderRow" data-sentry-source-file="index.tsx">{t("chart-customization.axis-assignment")}</HeaderRow>
        <div className={styles.axisSettingsBlock}>
          {showClauses.map((clause, clauseIdx) => {
          var _behavior$behaviors;
          const behavior = clause as BehaviorShowClause === null || clause as BehaviorShowClause === void 0 ? void 0 : (clause as BehaviorShowClause).behavior;
          const hasNestedBehaviors = isBehaviorShowClause(clause) && (behavior === null || behavior === void 0 || (_behavior$behaviors = behavior.behaviors) === null || _behavior$behaviors === void 0 ? void 0 : _behavior$behaviors.length) > 0;
          return getClauseRow(hasNestedBehaviors, clauseIdx, clause);
        })}
        </div>
      </div>
    </div>;
}